<template>
  <div class="flex flex-col gap-10">
    <ContentRow
      v-if="filteredModCategories.length > 0"
      v-for="(category, index) in filteredModCategories"
      :key="category.id"
      :mods="category.mods"
      :title="category.name"
      :icon="category.slug"
      :iconColor="category.color"
      @selectMod="selectMod"
      :selectedMod="selectedMod"
      :showCategory="false"
      :limitMods="false"
    />
  </div>
</template>

<script setup>
import {
  ref, computed, watch, nextTick,
} from 'vue';
import { useStore } from 'vuex';
import { useAuth } from '@websanova/vue-auth/src/v3.js';
import ContentRow from './ContentRow.vue';

const store = useStore();
const auth = useAuth();
const currentUser = ref(auth.user());

const props = defineProps({
  modCategories: {
    type: Array,
    required: true,
  },
  selectedMod: {
    type: Object,
    required: true,
  },
  searchInput: {
    type: String,
    default: '',
  },
  activeCategories: {
    type: Array,
    default: () => [],
  },
  activePlayerCounts: {
    type: Array,
    default: () => [],
  },
  activeTags: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['selectMod']);

function selectMod(mod) {
  emit('selectMod', mod);
}

// Auto-scroll to selected mod when component mounts or selectedMod changes
function scrollToSelectedMod() {
  if (!props.selectedMod) return;

  nextTick(() => {
    const modBlock = document.querySelector(`[data-mod-id="${props.selectedMod.id}"]`);

    if (modBlock) {
      const contentRow = modBlock.closest('[data-content-row]');

      // Check if this is the first ContentRow
      const allContentRows = document.querySelectorAll('[data-content-row]');
      const isFirstRow = allContentRows.length > 0 && contentRow === allContentRows[0];
      if (isFirstRow) {
        return;
      }
      // Find the actual scrollable container (the main content div in DashboardLayout)
      const scrollableContainer = document.querySelector('main .overflow-y-auto');

      if (scrollableContainer) {
        const containerRect = scrollableContainer.getBoundingClientRect();
        const modBlockRect = modBlock.getBoundingClientRect();

        const scrollOffset = modBlockRect.top - containerRect.top - 300;
        scrollableContainer.scrollTo({
          top: scrollableContainer.scrollTop + scrollOffset,
          behavior: 'smooth',
        });
      }
    }
  });
}

// Watch for selectedMod changes and scroll to it
watch(() => props.selectedMod, () => {
  scrollToSelectedMod();
}, { immediate: true });

// Expose the scroll function for parent component
defineExpose({
  scrollToSelectedMod,
});

const filteredModCategories = computed(() => {
  // First, filter all mod categories to only include playable CS2 mods
  let filteredCategories = props.modCategories.map((category) => ({
    ...category,
    mods: category.mods.filter((mod) => {
      if (!mod.playable || !mod.cs2) return false;
      if (mod.name && mod.name.toLowerCase() === 'scrim' && (!currentUser.value || currentUser.value.access_level <= 1)) return false;
      return true;
    }),
  })).filter((category) => category.mods.length > 0);

  // Filter by selected categories if any are selected
  if (props.activeCategories.length > 0) {
    filteredCategories = filteredCategories.filter((category) => props.activeCategories.includes(category.name));
  }

  // Filter by selected player counts if any are selected
  if (props.activePlayerCounts.length > 0) {
    filteredCategories = filteredCategories.map((category) => {
      const filteredMods = category.mods.filter((mod) => {
        const modPlayerCount = mod.player_count || 1;
        const modMinimumPlayerCount = mod.minimum_player_count || modPlayerCount;

        // Check if this mod's player count or minimum player count falls within any selected range
        return props.activePlayerCounts.some((selectedRange) => (modPlayerCount >= selectedRange.min && modPlayerCount <= selectedRange.max)
                 || (modMinimumPlayerCount >= selectedRange.min && modMinimumPlayerCount <= selectedRange.max));
      });

      if (filteredMods.length > 0) {
        return {
          ...category,
          mods: filteredMods,
        };
      }
      return null;
    }).filter((category) => category !== null);
  }

  // Filter by selected tags if any are selected
  if (props.activeTags.length > 0) {
    filteredCategories = filteredCategories.map((category) => {
      const filteredMods = category.mods.filter((mod) => {
        // Get all tags for this mod
        const modTags = [];
        if (mod.tags && Array.isArray(mod.tags)) {
          mod.tags.forEach((tag) => {
            if (tag.name) {
              modTags.push(tag.name);
            }
          });
        }

        // Check if this mod has all of the selected tags
        return props.activeTags.every((selectedTag) => modTags.includes(selectedTag));
      });

      if (filteredMods.length > 0) {
        return {
          ...category,
          mods: filteredMods,
        };
      }
      return null;
    }).filter((category) => category !== null);
  }

  // Filter by search input if provided
  if (props.searchInput && props.searchInput.trim()) {
    const searchTerm = props.searchInput.toLowerCase().trim();

    filteredCategories = filteredCategories
      .map((category) => {
        // Filter mods within this category that match the search term
        const filteredMods = category.mods.filter((mod) => mod.name.toLowerCase().includes(searchTerm));

        // Return category with filtered mods if any mods match
        if (filteredMods.length > 0) {
          return {
            ...category,
            mods: filteredMods,
          };
        }
        return null;
      })
      .filter((category) => category !== null); // Remove categories with no matching mods
  } else {
    // If no search term, just filter out categories with no mods
    filteredCategories = filteredCategories.filter((category) => category.mods.length > 0);
  }

  return filteredCategories;
});

</script>
